import {
  type CellData,
  columnFilteringFeature,
  columnPinningFeature,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createColumnHelper,
  createFilteredRowModel,
  createSortedRowModel,
  filterFn_equals,
  filterFn_greaterThan,
  filterFn_greaterThanOrEqualTo,
  filterFn_includesString,
  filterFn_lessThan,
  filterFn_lessThanOrEqualTo,
  filterFn_startsWith,
  globalFilteringFeature,
  metaHelper,
  type RowData,
  rowSelectionFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_basic,
  sortFn_text,
  tableFeatures,
} from "@tanstack/react-table";

/**
 * The one TanStack Table feature set every dashboard `DataTable` runs on: sorting, a global
 * search, a filter builder, resizable and pinned columns, and row selection. A page
 * declares columns with {@link createDataTableColumns} and never touches the table instance.
 */

/**
 * One operator in the toolbar's filter builder. `fn` is a built-in filter function; `value`
 * says what the user types, and `empty` operators (is empty / is not empty) take nothing.
 */
export interface DataTableFilterOperator {
  id: string;
  label: string;
  fn: "includesString" | "equals" | "startsWith" | "greaterThan" | "lessThan";
  value: "text" | "number" | "empty";
}

/** Text columns: contains, is, starts with, is empty, is not empty. */
export const TEXT_FILTER_OPERATORS: DataTableFilterOperator[] = [
  { id: "contains", label: "contains", fn: "includesString", value: "text" },
  { id: "is", label: "is", fn: "equals", value: "text" },
  { id: "startsWith", label: "starts with", fn: "startsWith", value: "text" },
  { id: "empty", label: "is empty", fn: "equals", value: "empty" },
  { id: "notEmpty", label: "is not empty", fn: "greaterThan", value: "empty" },
];

/** Number columns: equals and the four comparisons. */
export const NUMBER_FILTER_OPERATORS: DataTableFilterOperator[] = [
  { id: "eq", label: "=", fn: "equals", value: "number" },
  { id: "gt", label: ">", fn: "greaterThan", value: "number" },
  { id: "gte", label: "≥", fn: "greaterThan", value: "number" },
  { id: "lt", label: "<", fn: "lessThan", value: "number" },
  { id: "lte", label: "≤", fn: "lessThan", value: "number" },
];

/** What a column tells the `DataTable` beyond how to render it. */
export interface DataTableColumnMeta {
  /**
   * Offers the column in the filter builder. `text` compares strings, `number` compares the
   * raw number (a money column filtered in dollars sets `filterCents` so the input is dollars).
   */
  filter?: "text" | "number";
  /** The number filter input is dollars; the column's raw value is cents. */
  filterCents?: boolean;
  /** Toolbar label for the filter, and the CSV header; defaults to the column header when it is text, else the id. */
  label?: string;
  /**
   * The CSV cell. `false` leaves the column out; a function formats the accessor value;
   * absent writes the accessor value as text. A display column (no accessor) is never exported.
   */
  csv?: false | ((value: CellData) => string);
  /** Header cell alignment; cells follow. */
  align?: "start" | "end";
  /** Not rendered, only exported: a column that exists for the CSV. */
  hidden?: boolean;
}

export const dataTableFeatures = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns: { alphanumeric: sortFn_alphanumeric, basic: sortFn_basic, text: sortFn_text },
  columnFilteringFeature,
  globalFilteringFeature,
  filteredRowModel: createFilteredRowModel(),
  filterFns: {
    includesString: filterFn_includesString,
    equals: filterFn_equals,
    startsWith: filterFn_startsWith,
    greaterThan: filterFn_greaterThan,
    greaterThanOrEqualTo: filterFn_greaterThanOrEqualTo,
    lessThan: filterFn_lessThan,
    lessThanOrEqualTo: filterFn_lessThanOrEqualTo,
  },
  columnSizingFeature,
  columnResizingFeature,
  columnVisibilityFeature,
  columnPinningFeature,
  rowSelectionFeature,
  columnMeta: metaHelper<DataTableColumnMeta>(),
});

export type DataTableFeatures = typeof dataTableFeatures;

/** Selected row ids → `true`; the shape `DataTable`'s `rowSelection` prop takes. */
export type { RowSelectionState } from "@tanstack/react-table";

/** A column helper bound to the shared feature set; call it once at module scope per table. */
export function createDataTableColumns<TData extends RowData>() {
  return createColumnHelper<DataTableFeatures, TData>();
}
